// TE map-pane "draw an area" filter wiring (T15 polygon). Extracted from TableExplorerTab (already
// 3k+ lines) so the draw state + chip-apply logic lives in one small, testable unit. Mirrors the
// bbox "Filter by map view" flow: it REPLACES any existing intersects_polygon chip on the spatial
// column with the freshly-drawn ring, then exits draw mode.

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { TrackFunction } from '../../../../core/analytics';
import { toast } from '../../../../components/ui/toaster';
import type { FilterCondition, FilterGroup, SpatialFilterContext } from '../../core/types/workspaceTypes';
import type { SpatialDrawContract } from '../../spatial/map/spatialMapTypes';
import { canEmitSpatialSubOperator } from '../../common/utils/spatialFilterSql';
import { validatePolygonRing, type RingRejectReason } from '../../common/utils/spatialPolygonRing';

export type SpatialFilterColumnInfo = { name: string; kind: SpatialFilterContext['kind']; lngColumn?: string };

const REJECT_MESSAGE: Record<RingRejectReason, string> = {
  'too-few-vertices': 'A drawn area needs at least three points.',
  'non-finite': 'The drawn area had invalid coordinates.',
  'lat-out-of-range': 'The drawn area went outside valid latitudes.',
  'lng-out-of-range': 'The drawn area went outside valid longitudes.',
  antimeridian: 'Areas that cross the ±180° meridian are not supported yet.',
  degenerate: 'The drawn points were on a straight line — draw an area with some width.',
  'self-intersecting': 'The drawn outline crossed itself — draw a simple (non-crossing) area.',
  'too-many-vertices': 'The drawn area had too many points.',
};

export function useSpatialDrawFilter(params: {
  spatialFilterColumn: SpatialFilterColumnInfo | null;
  spatialColumnSrids: number[];
  userFilters: readonly FilterGroup[];
  onFiltersChange: (filters: FilterGroup[]) => void;
  engineId: DatabaseEngineId;
  track: TrackFunction;
}): { drawActive: boolean; canDraw: boolean; toggleDraw: () => void; draw: SpatialDrawContract } {
  const { spatialFilterColumn, spatialColumnSrids, userFilters, onFiltersChange, engineId, track } = params;
  const [drawActive, setDrawActive] = useState(false);

  const srid = spatialColumnSrids.length === 1 ? spatialColumnSrids[0] : null;
  const ctx: SpatialFilterContext | null = useMemo(
    () =>
      spatialFilterColumn
        ? spatialFilterColumn.kind === 'declared-pair'
          ? { kind: 'declared-pair', srid: 4326, lngColumn: spatialFilterColumn.lngColumn }
          : { kind: spatialFilterColumn.kind, srid }
        : null,
    [spatialFilterColumn, srid],
  );
  // Draw needs a KNOWN coordinate system: a null/mixed SRID renders the map planar (raw XY), where a
  // lng/lat ring would be meaningless — stricter than bbox (which is SRID-agnostic) on purpose.
  const canDraw =
    !!spatialFilterColumn && !!ctx && ctx.srid != null &&
    canEmitSpatialSubOperator(engineId, 'intersects_polygon', ctx);

  // Enter/leave draw mode. The mode is a toggle, so BOTH transitions fire the analytics event with
  // `{ enabled }` — including exits via finish/cancel/invalid, not just the button (repo rule).
  const startDraw = useCallback(() => {
    setDrawActive(true);
    track('feature:spatial_draw_mode', { enabled: true });
  }, [track]);
  const endDraw = useCallback(() => {
    setDrawActive((prev) => {
      if (prev) track('feature:spatial_draw_mode', { enabled: false });
      return false;
    });
  }, [track]);

  // Drop out of draw mode if the target column / capability goes away (table switch, column removed).
  useEffect(() => {
    if (drawActive && !canDraw) endDraw();
  }, [drawActive, canDraw, endDraw]);

  const onPolygonDrawn = useCallback(
    (ring: [number, number][]) => {
      endDraw();
      if (!spatialFilterColumn || !ctx) return;
      const validated = validatePolygonRing(ring);
      if (!validated.ok) {
        toast.error({ title: 'Could not use that area', description: REJECT_MESSAGE[validated.reason] });
        return;
      }
      const condition: FilterCondition = {
        operator: 'spatial',
        spatialSubOperator: 'intersects_polygon',
        polygon: validated.ring,
        spatialContext: ctx,
      };
      // Replace at the CONDITION level, not the group level: strip only the existing
      // intersects_polygon condition(s) on this column and drop a group only once it is empty, so a
      // group that also carries a bbox/distance chip (same column, joined via the chip's "+ OR")
      // keeps that sibling condition. Other columns' groups are untouched.
      const others = userFilters
        .map((g) =>
          g.field === spatialFilterColumn.name
            ? {
                ...g,
                conditions: g.conditions.filter(
                  (c) => !(c.operator === 'spatial' && c.spatialSubOperator === 'intersects_polygon'),
                ),
              }
            : g,
        )
        .filter((g) => g.conditions.length > 0);
      onFiltersChange([...others, { field: spatialFilterColumn.name, conditions: [condition] }]);
      track('feature:spatial_filter_apply', { kind: 'polygon', engine: engineId });
    },
    [endDraw, spatialFilterColumn, ctx, userFilters, onFiltersChange, engineId, track],
  );

  const toggleDraw = useCallback(() => {
    if (drawActive) endDraw();
    else startDraw();
  }, [drawActive, startDraw, endDraw]);

  return {
    drawActive,
    canDraw,
    toggleDraw,
    draw: { active: drawActive, onPolygonDrawn, onCancel: () => setDrawActive(false) },
  };
}
